/**
 *  @license
 *    Copyright 2018 Brigham Young University
 *
 *    Licensed under the Apache License, Version 2.0 (the "License");
 *    you may not use this file except in compliance with the License.
 *    You may obtain a copy of the License at
 *
 *        http://www.apache.org/licenses/LICENSE-2.0
 *
 *    Unless required by applicable law or agreed to in writing, software
 *    distributed under the License is distributed on an "AS IS" BASIS,
 *    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *    See the License for the specific language governing permissions and
 *    limitations under the License.
 **/
'use strict'
import axios from '~/plugins/axios'
import { site } from '~~/config'
import Vue from 'vue'

// ignore any components that start with byu-
const byuWebComponentNames = [
  /^byu-/,
]

// tell vue to ignore the BYU web components
if (!Array.isArray(Vue.config.ignoredElements)) Vue.config.ignoredElements = []
byuWebComponentNames.forEach(name => {
  if (Vue.config.ignoredElements.indexOf(name) === -1) Vue.config.ignoredElements.push(name)
})

// This plugin must exist outside of the exported function or it will not run server side.
Vue.use({
  install: function (Vue) {

    //////////////////////////////////////////////////////
    //                                                  //
    //      WATCH FOR ROUTE CHANGES                     //
    //                                                  //
    //////////////////////////////////////////////////////

    Vue.mixin({

      // if component has byuNavigation property then update nav
      beforeRouteEnter (to, from, next) {
        next(vm => routeEnter(vm, vm.$route, from))
      },

      // if component has byuNavigation property then update nav
      beforeRouteUpdate (to, from, next) {
        routeEnter(this, to, from)
        next()
      }

    })
  }
})

export default async function ({ app, store }) {
  if (process.client) {
    try {
      const response = await axios.get('/api/auth/session')
      store.commit('wabs/sessionUpdate', response.data)
    } catch (err) {
      console.error('Unable to load the browser session', err)
      store.commit('wabs/sessionUpdate', { authenticated: false, user: null })
    }
  } else {
    store.commit('wabs/sessionUpdate', { authenticated: false, user: null })
  }

  Vue.use({
    install: function (Vue) {
      const byu = process.server ? {} : (window.byu || (window.byu = {}))

      if (!byu.auth) byu.auth = {}

      Object.defineProperty(byu, 'user', {
        configurable: true,
        get: () => store.state.wabs.user
      })

      byu.auth.login = function () {
        if (process.server) return
        const currentRoute = window.location.pathname + window.location.search + window.location.hash
        window.location.assign('/signin?redirect=' + encodeURIComponent(currentRoute))
      }

      byu.auth.logout = function () {
        if (process.server) return
        window.location.assign('/signout')
      }

      byu.auth.request = function (options, callback) {
        const target = new URL(options.url)

        const proxyRequest = axios.request({
          method: options.method || 'GET',
          url: '/proxy' + target.pathname + target.search,
          headers: options.headers || {},
          data: options.body,
          validateStatus: () => true
        }).then(response => {
          if (response.status === 401 && typeof window !== 'undefined') {
            const currentRoute = window.location.pathname + window.location.search + window.location.hash
            window.location.assign('/signin?redirect=' + encodeURIComponent(currentRoute))
          }

          return {
            status: response.status,
            body: typeof response.data === 'string'
              ? response.data
              : JSON.stringify(response.data)
          }
        })

        if (typeof callback === 'function') {
          proxyRequest
            .then(result => callback(result.body, result.status))
            .catch(error => callback(error.message, 500))

          return
        }

        return proxyRequest
      }


      let searchTimeout
      byu.search = {
        submit (value, autoSearch) {
          clearTimeout(searchTimeout)
          if (this.value !== value) this.value = value
          if (typeof this.handler === 'function') {
            this.handler(value, !!autoSearch)
          } else {
            // console.log('You need to set a search handler function on this.$byu.search.handler. Searched for "' +
            //     value + '"' + (autoSearch ? ' using debounced auto search' : ''));
          }
        }
      }
      Object.defineProperties(byu.search, {
        disabled: {
          get: () => typeof store.state.byu.searchHandler !== 'function',
        },

        handler: {
          get: () => store.state.byu.searchHandler,
          set (value) {
            clearTimeout(searchTimeout)
            store.commit('byu/searchHandlerUpdate', value)
          }
        },

        value: {
          get: () => store.state.byu.searchValue,
          set (value) {
            clearTimeout(searchTimeout)
            store.commit('byu/searchValueUpdate', value)

            const handler = this.handler
            const debounce = handler && handler.debounce
              ? handler.debounce
              : site.searchDebounce

            if (debounce > 0) {
              searchTimeout = setTimeout(() => this.submit(value, true), debounce)
            }
          }
        }
      })

      app.$byu = byu

      Object.defineProperty(Vue.prototype, '$byu', {
        configurable: true,
        value: byu
      })
    }
  })
}

let firstRouteEnter = true

function routeEnter (vm, to, from) {
  const titleCreator = vm.$options.pageTitle
  if (typeof titleCreator === 'function') {
    const previous = firstRouteEnter
      ? site.pageTitle
      : vm.$store.state.byu.pageTitle
    const newTitle = titleCreator.call(vm, { to, from, previous })
    vm.$store.commit('byu/updateTitle', newTitle)
  } else {
    vm.$store.commit('byu/updateTitle', site.pageTitle)
  }

  const searchCreator = vm.$options.pageSearch
  if (typeof searchCreator === 'function') {
    const previous = firstRouteEnter
      ? site.searchHandler
      : vm.$byu.search.handler
    const newSearch = searchCreator.call(vm, { to, from, previous })
    vm.$byu.search.handler = typeof newSearch === 'function' ? newSearch : null
  } else {
    vm.$byu.search.handler = site.searchHandler
  }
  vm.$byu.search.value = ''

  const navCreator = vm.$options.navigationMenu
  if (typeof navCreator === 'function') {
    const previous = firstRouteEnter
      ? site.navigationLinks
      : vm.$store.state.byu.navigationLinks
    const newLinks = navCreator.call(vm, { to, from, previous })
    vm.$store.commit('byu/updateNavigation', newLinks)
  } else {
    vm.$store.commit('byu/updateNavigation', site.navigationLinks)
  }

  firstRouteEnter = false
}
